import type { ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { RewriteFormValue } from '../../rewriteForm';
import { rewriteFields } from '../../rewriteFields';

export function RewriteSetRow({
  ruleIndex,
  setIndex,
  canRemove,
  onRemove,
}: {
  ruleIndex: number;
  setIndex: number;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const form = useFormContext<RewriteFormValue>();
  const ruleKey = ruleIndex.toString();
  const setKey = setIndex.toString();
  const prefix = `rules.${ruleKey}.sets.${setKey}` as `rules.${number}.sets.${number}`;
  const value = form.watch(`${prefix}.value`);
  const valueRegistration = form.register(`${prefix}.value`);
  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_36px] items-center gap-3 lg:grid-cols-[1.4fr_1fr_auto_36px]">
      <Controller
        control={form.control}
        name={`${prefix}.field`}
        render={({ field }) => (
          <Select value={field.value} onValueChange={field.onChange}>
            <SelectTrigger
              className="col-span-2 h-9 lg:col-span-1"
              aria-label={dashboardT('lists.rewrite.field')}
            >
              <SelectValue placeholder={dashboardT('lists.rewrite.field')} />
            </SelectTrigger>
            <SelectContent>
              {rewriteFields.map((item) => (
                <SelectItem key={item} value={item}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
      <Input
        className="col-span-2 h-9 lg:col-span-1"
        {...valueRegistration}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          void valueRegistration.onChange(event);
          const hasValue = Boolean(event.currentTarget.value.trim());
          if (hasValue) form.setValue(`${prefix}.nullify`, false);
          else form.setValue(`${prefix}.overwrite`, false);
        }}
        placeholder={dashboardT('lists.rewrite.value')}
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className="flex items-center gap-2 text-sm">
          <Controller
            control={form.control}
            name={`${prefix}.overwrite`}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                disabled={!value.trim()}
                onCheckedChange={(checked) => {
                  field.onChange(checked === true);
                }}
              />
            )}
          />
          {dashboardT('lists.rewrite.overwrite')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Controller
            control={form.control}
            name={`${prefix}.nullify`}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                disabled={Boolean(value.trim())}
                onCheckedChange={(checked) => {
                  field.onChange(checked === true);
                }}
              />
            )}
          />
          {dashboardT('lists.rewrite.nullify')}
        </label>
      </div>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-9 w-9"
        disabled={!canRemove}
        onClick={onRemove}
        aria-label={dashboardT('lists.rewrite.removeSet')}
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
